import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowRight, Search, Loader2, Droplets, Disc, Wrench, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { articlesAPI, uploadsAPI } from '../services/api';
import { usePageMeta } from '../services/seo';
import { useT } from '../services/i18n';
import { localizeArticle } from '../services/catalogI18n';

// Popular themes: counted from the real articles (title + description) in the current language
const POPULAR = [
  { key: 'oil', labelKey: 'blog.catOil', Icon: Droplets, keywords: { fr: ['huile'], en: ['oil'], de: ['öl'], es: ['aceite'], it: ['olio'] } },
  { key: 'brakes', labelKey: 'blog.catBrakes', Icon: Disc, keywords: { fr: ['frein'], en: ['brake'], de: ['brems'], es: ['freno'], it: ['fren'] } },
  { key: 'maintenance', labelKey: 'blog.catMaintenance', Icon: Wrench, keywords: { fr: ['entretien'], en: ['maintenance', 'service'], de: ['wartung'], es: ['mantenimiento'], it: ['manutenzione'] } },
  { key: 'electric', labelKey: 'blog.catElectric', Icon: Zap, keywords: { fr: ['électri', 'batterie'], en: ['electric', 'battery'], de: ['elektri', 'batterie'], es: ['eléctri', 'batería'], it: ['elettri', 'batteria'] } },
];

const ArticlesListPage = () => {
  const { t, lang } = useT();
  usePageMeta({ title: t('blog.title'), description: t('blog.intro'), path: '/articles' });
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const data = await articlesAPI.getAll();
      setArticles(data);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredArticles = articles.map(a => localizeArticle(a, lang)).filter(article =>
    article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const popularCategories = POPULAR.map(c => {
    const keywords = c.keywords[lang] || c.keywords.fr;
    const count = articles.map(a => localizeArticle(a, lang)).filter(a => keywords.some(k => `${a.title || ''} ${a.description || ''}`.toLowerCase().includes(k))).length;
    return { ...c, keywords, count };
  }).filter(c => c.count > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('blog.title')}</h1>
          <p className="text-gray-300 max-w-2xl">
            {t('blog.intro')}
          </p>
          
          {/* Search */}
          <div className="mt-6 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('blog.searchPh')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-12">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500">{t('blog.empty')}</p>
          </div>
        ) : (
          <>
            {/* Featured Article */}
            {filteredArticles.length > 0 && !searchTerm && (
              <div className="mb-12">
                <Link 
                  to={`/article/${filteredArticles[0].id}`}
                  className="block bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="aspect-[16/10] md:aspect-auto">
                      <img 
                        src={uploadsAPI.resolveUrl(filteredArticles[0].image)} 
                        alt={filteredArticles[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6 md:p-8 flex flex-col justify-center">
                      <span className="inline-flex items-center gap-1 text-orange-500 text-sm font-medium mb-3">
                        <span className="w-8 h-0.5 bg-orange-500"></span>
                        {t('blog.featured')}
                      </span>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-orange-500 transition-colors">
                        {filteredArticles[0].title}
                      </h2>
                      <p className="text-gray-600 mb-6 line-clamp-3">
                        {filteredArticles[0].description}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {filteredArticles[0].author || 'Expert OTOPIECES'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {filteredArticles[0].date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {filteredArticles[0].read_time || '5 min'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Articles Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(searchTerm ? filteredArticles : filteredArticles.slice(1)).map(article => (
                <Link 
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={uploadsAPI.resolveUrl(article.image)} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {article.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {article.read_time || '5 min'}
                        </span>
                      </div>
                      <span className="text-orange-500 group-hover:translate-x-1 transition-transform">
                        <ArrowRight size={18} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Categories */}
            {popularCategories.length > 0 && (
              <div className="mt-16" data-testid="blog-popular-categories">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('blog.popular')}</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {popularCategories.map((category) => (
                    <button
                      type="button"
                      key={category.key}
                      data-testid={`blog-category-${category.key}`}
                      onClick={() => setSearchTerm(category.keywords[0])}
                      className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-left"
                    >
                      <category.Icon size={24} className="text-orange-500 mb-2" />
                      <p className="font-medium text-gray-900">{t(category.labelKey)}</p>
                      <p className="text-sm text-gray-500">{t('blog.nArticles', { n: category.count, count: category.count })}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ArticlesListPage;
